import * as React from "react";

import { cn } from "@/engine/support/classNames";

import { ANIMALS, colourValue, type AnimalId, type ColourId } from "./campData";

export interface AnimalHeadProps extends React.HTMLAttributes<HTMLSpanElement> {
  animal: AnimalId;
  colour?: ColourId | undefined;
  /** A token colour that replaces `colour` where the disc carries a tone, not a person. */
  tint?: string | undefined;
  size?: number | undefined;
  speaking?: boolean | undefined;
  muted?: boolean | undefined;
  dim?: boolean | undefined;
  you?: boolean | undefined;
}

function animalStateClass(muted: boolean, dim: boolean): string | undefined {
  return muted || dim ? "opacity-45" : undefined;
}

/** The ring, the speaking glow and a soft shadow, all in the person's colour. */
function animalRing(identity: string, speaking: boolean): string {
  const width = speaking ? 3 : 2;
  const glow = speaking ? 16 : 0;
  const shadow = `0 4px 10px color-mix(in srgb, ${identity} 40%, transparent)`;
  return `0 0 0 ${width}px ${identity}, 0 0 ${glow}px ${identity}, ${shadow}`;
}

const shapes: Record<AnimalId, React.ReactNode> = {
  fox: (
    <>
      <path
        d="M5.2 9.6 6.7 2.9 11.3 7.3ZM18.8 9.6 17.3 2.9 12.7 7.3Z"
        fill="currentColor"
      />
      <path
        d="M12 5.6c4.6 0 7 3.6 7 7.2 0 4-3.1 7.2-7 7.2s-7-3.2-7-7.2c0-3.6 2.4-7.2 7-7.2Z"
        fill="currentColor"
      />
    </>
  ),
  owl: (
    <>
      <path
        d="M6.6 6.6 5.1 2.6 9.4 5.4ZM17.4 6.6 18.9 2.6 14.6 5.4Z"
        fill="currentColor"
      />
      <path
        d="M12 4.9c4.1 0 7 3 7 7.1 0 4.5-3.1 8-7 8s-7-3.5-7-8c0-4.1 2.9-7.1 7-7.1Z"
        fill="currentColor"
      />
    </>
  ),
  bear: (
    <>
      <circle cx="8" cy="7" r="3" fill="currentColor" />
      <circle cx="16" cy="7" r="3" fill="currentColor" />
      <path
        d="M12 6.4c4.2 0 6.8 3 6.8 6.7 0 4-3 7-6.8 7s-6.8-3-6.8-7c0-3.7 2.6-6.7 6.8-6.7Z"
        fill="currentColor"
      />
    </>
  ),
  hare: (
    <>
      <path
        d="M9.4 9.2C8.1 6 8 3 9.5 2.5S11.9 5.4 12 9.2ZM14.6 9.2c1.3-3.2 1.4-6.2-.1-6.7S12.1 5.4 12 9.2Z"
        fill="currentColor"
      />
      <path
        d="M12 9c3.6 0 6 2.6 6 5.7 0 3.4-2.6 5.9-6 5.9s-6-2.5-6-5.9C6 11.6 8.4 9 12 9Z"
        fill="currentColor"
      />
    </>
  ),
  wolf: (
    <>
      <path
        d="M5.4 10 6.4 3.2 11 7.6ZM18.6 10 17.6 3.2 13 7.6Z"
        fill="currentColor"
      />
      <path
        d="M12 6c4.3 0 6.8 3.2 6.8 6.6 0 3.6-2.2 6.3-4.6 7.4L12 21l-2.2-1c-2.4-1.1-4.6-3.8-4.6-7.4C5.2 9.2 7.7 6 12 6Z"
        fill="currentColor"
      />
    </>
  ),
  turtle: (
    <path
      d="M12 6.6c4.4 0 7.4 3.4 7.4 7.1 0 1-.8 1.7-1.8 1.7H6.4c-1 0-1.8-.7-1.8-1.7 0-3.7 3-7.1 7.4-7.1ZM7 16h10v4H7Z"
      fill="currentColor"
    />
  ),
  deer: (
    <>
      <path
        d="M9.2 7 8 3.6 6.2 4.4M8 3.6 9.7 2.7M14.8 7 16 3.6 17.8 4.4M16 3.6 14.3 2.7"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M12 7.4c3.4 0 5.7 2.6 5.7 5.7 0 3.7-2.5 7.1-5.7 7.1s-5.7-3.4-5.7-7.1c0-3.1 2.3-5.7 5.7-5.7Z"
        fill="currentColor"
      />
    </>
  ),
  cat: (
    <>
      <path
        d="M5.6 9.6 5.5 3.6 10.5 6.9ZM18.4 9.6 18.5 3.6 13.5 6.9Z"
        fill="currentColor"
      />
      <path
        d="M12 6.2c4.3 0 6.9 3.1 6.9 6.8 0 3.9-3 6.9-6.9 6.9s-6.9-3-6.9-6.9c0-3.7 2.6-6.8 6.9-6.8Z"
        fill="currentColor"
      />
    </>
  ),
  frog: (
    <>
      <circle cx="8.4" cy="8" r="3.1" fill="currentColor" />
      <circle cx="15.6" cy="8" r="3.1" fill="currentColor" />
      <path
        d="M12 9.4c4.6 0 7.4 2.4 7.4 5.3 0 3-3.2 5.3-7.4 5.3s-7.4-2.3-7.4-5.3c0-2.9 2.8-5.3 7.4-5.3Z"
        fill="currentColor"
      />
    </>
  ),
  hedge: (
    <path
      d="m4 18 1-5-2-2 4-1V6l3 2 2-5 2 5 3-2v4l4 1-2 2 1 5-4-1c-1 2-2.4 3-4 3s-3-1-4-3Z"
      fill="currentColor"
    />
  ),
  mouse: (
    <>
      <circle cx="7.3" cy="8.2" r="3.3" fill="currentColor" />
      <circle cx="16.7" cy="8.2" r="3.3" fill="currentColor" />
      <path
        d="M12 9.6c3.4 0 5.7 2.4 5.7 5.1 0 3-2.6 5.4-5.7 5.4s-5.7-2.4-5.7-5.4c0-2.7 2.3-5.1 5.7-5.1Z"
        fill="currentColor"
      />
    </>
  ),
  raccoon: (
    <>
      <path
        d="M6.3 8.6a2.4 2.4 0 1 1 3.8-2.8 2.4 2.4 0 0 1-3.8 2.8ZM17.7 8.6a2.4 2.4 0 1 0-3.8-2.8 2.4 2.4 0 0 0 3.8 2.8Z"
        fill="currentColor"
      />
      <path
        d="M12 6.4c4.2 0 6.9 3 6.9 6.6 0 4-3 7-6.9 7s-6.9-3-6.9-7c0-3.6 2.7-6.6 6.9-6.6Z"
        fill="currentColor"
      />
    </>
  ),
};

/** A purpose-drawn animal head for lists, tiles and compact identity controls. */
export const AnimalHead = React.forwardRef<HTMLSpanElement, AnimalHeadProps>(
  function AnimalHead(
    {
      animal,
      colour = "ash",
      tint,
      size = 34,
      speaking = false,
      muted = false,
      dim = false,
      you = false,
      className,
      ...props
    },
    ref,
  ) {
    const natural =
      ANIMALS.find((entry) => entry.id === animal)?.naturalColour ??
      "hsl(var(--animal-fox))";
    const identity = tint ?? colourValue(colour);
    return (
      <span
        ref={ref}
        className={cn(
          "relative inline-grid shrink-0 place-items-center rounded-full transition",
          animalStateClass(muted, dim),
          className,
        )}
        style={{
          width: size,
          height: size,
          backgroundColor: `color-mix(in srgb, ${identity} 24%, transparent)`,
          boxShadow: animalRing(identity, speaking),
        }}
        {...props}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="size-[72%]"
          style={{ color: natural }}
        >
          {shapes[animal]}
          <ellipse
            cx="12"
            cy="16"
            rx="3.3"
            ry="2.6"
            fill="var(--animal-muzzle, hsl(var(--primary-foreground) / .32))"
          />
          <circle cx="9.6" cy="12.6" r="1" fill="hsl(var(--overlay))" />
          <circle cx="14.4" cy="12.6" r="1" fill="hsl(var(--overlay))" />
          <path d="m12 15 1 1h-2Z" fill="hsl(var(--overlay))" />
        </svg>
        {you ? (
          <span className="absolute -right-0.5 -bottom-0.5 size-2 rounded-full bg-you ring-2 ring-background" />
        ) : null}
      </span>
    );
  },
);
